/*
This computer program, as defined in the Copyright, Designs and Patents Act 1998 and the Software Directive (2009/24/EC), 
is the copyright of Logic Valley Ltd, a wholly owned subsidiary of Marston (Holdings) Ltd. All rights are reserved.
*/
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import './OffenceDetailsSection.css'
import AutoComplete from '@components/base/autoComplete/AutoComplete'
import { ReactSVG } from 'react-svg'
import EnableEditIcon from '../../../../../../src/assets/images/vrm_edit_enable.svg'
import SaveIcon from '@mui/icons-material/Save'
import TruncatedText from '../TruncatedText'
import {
  selectedContractDetails,
  toastMessage,
  madeChanges,
} from '@components/recoil/atoms/atoms'
import { fetchDataSelector } from '@components/recoil/selectors/selectors'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import utils from '@utils/utils'
import { ManualReviewAPIEndpoints } from '@pages/manualReviewTab/api'
import { fetchLookupDataSelector } from '@components/recoil/selectors/lookupSelectors'
import { useLocation } from 'react-router-dom'
import { TASK_STATUS } from '@pages/manualReviewTab/constants'

const OffenceDetailsSection = ({
  labels,
  values,
  onSaveContraventionCode,
  observationDetails,
}) => {
  const [isEditingContravention, setIsEditingContravention] = useState(false)
  const [displayContraventionCode, setDisplayContraventionCode] = useState(
    values?.contraventionCode || ''
  )
  const [contraventionValue, setContraventionValue] = useState({
    id: observationDetails?.contraventionId,
    codeWithDescription:
      observationDetails?.offenceDetails?.contraventionCode || '',
  })
  const [contraventionCodeDatasource, setContraventionCodeDatasource] =
    useState([])
  const getCoreLookupData = useRecoilValue(fetchLookupDataSelector)
  const [displayContraventionLocation, setDisplayContraventionLocation] =
    useState(values?.contraventionLocation || '')

  const getCoreData = useRecoilValue(fetchDataSelector)
  const selectedContractData = useRecoilValue(selectedContractDetails)
  const showToastMessage = useSetRecoilState(toastMessage)
  const setMadeChanges = useSetRecoilState(madeChanges)
  const location = useLocation()
  const effectiveContractId = useMemo(() => {
    if (selectedContractData?.contractId) {
      return selectedContractData.contractId
    }
    const queryContractId = new URLSearchParams(location?.search).get(
      'contractId'
    )
    if (!queryContractId) return ''
    try {
      return utils?.decryptData(queryContractId) || queryContractId
    } catch {
      return queryContractId
    }
  }, [selectedContractData?.contractId, location?.search])

  const handleEditClick = () => {
    setIsEditingContravention(true)
  }

  const handleSaveClick = async () => {
    const selectedContraventionCode = contraventionValue?.code || ''

    if (
      !selectedContraventionCode ||
      selectedContraventionCode === displayContraventionCode
    ) {
      return
    }

    if (onSaveContraventionCode) {
      try {
        const response = await getCoreData(
          ManualReviewAPIEndpoints?.updateContraventionCode?.method,
          `${ManualReviewAPIEndpoints?.updateContraventionCode?.url
            .replace('__observationId__', values?.observationId)
            .replace('__contraventionId__', contraventionValue?.id)
            .replace('__contraventionCode__', selectedContraventionCode)}`,
          null,
          null,
          null,
          false,
          utils?.apiDomainName?.privateLand?.name
        )
        if (response?.status === utils?.apiResponseCode?.successCode) {
          const newCodeWithDescription =
            contraventionValue?.codeWithDescription || ''
          setDisplayContraventionCode(newCodeWithDescription)
          const locationName =
            displayContraventionLocation?.split(' / ')?.[0] ||
            values?.contraventionLocation
          const newContraventionCode =
            contraventionValue?.code ||
            contraventionValue?.codeWithDescription?.split(' ')?.[0] ||
            ''
          if (locationName && newContraventionCode) {
            setDisplayContraventionLocation(
              `${locationName} / ${newContraventionCode}`
            )
          }

          showToastMessage({
            message: 'Contravention Code updated successfully.',
            status: 'success',
          })
          setMadeChanges(false)
          onSaveContraventionCode(contraventionValue)
          setIsEditingContravention(false)
        }
      } catch (error) {
        showToastMessage({
          message: 'Failed to update contravention code.',
          status: 'error',
        })
      }
    }
  }

  const handleContraventionChange = (event, params) => {
    const newValue = params?.selectedDataSource
    setContraventionValue(newValue)
    const newCode = newValue?.codeWithDescription || newValue?.code || ''
    setMadeChanges(newCode !== displayContraventionCode)
  }

  const selectedContraventionCode =
    contraventionValue?.codeWithDescription || contraventionValue?.code || ''
  const isSaveDisabled =
    !selectedContraventionCode ||
    selectedContraventionCode === displayContraventionCode

  /**
   * Fetch contravention location data
   */
  /* istanbul ignore next */
  const getContraventionLocationData = useCallback(async () => {
    const contraventionId = observationDetails?.contraventionId
    const locationId = values?.locationId
    if (!effectiveContractId || !contraventionId || !locationId) return
    try {
      const response = await getCoreLookupData(
        ManualReviewAPIEndpoints?.getContraventionLocation?.method,
        `${ManualReviewAPIEndpoints?.getContraventionLocation?.url
          .replace('__contractId__', effectiveContractId)
          .replace('__contraventionId__', contraventionId)
          .replace('__locationId__', locationId)}`
      )
      if (response?.status === utils?.apiResponseCode?.successCode) {
        const { locationName, contraventionCode, codeWithDescription } =
          response?.data || {}
        setDisplayContraventionLocation(
          locationName && contraventionCode
            ? `${locationName} / ${contraventionCode}`
            : values?.contraventionLocation || ''
        )
        setDisplayContraventionCode(codeWithDescription || '')
      }
    } catch {
      showToastMessage({
        message: 'Failed to load contravention location details.',
        status: 'error',
      })
    }
  }, [
    getCoreLookupData,
    showToastMessage,
    effectiveContractId,
    observationDetails?.contraventionId,
    values?.locationId,
    values?.contraventionLocation,
  ])

  /**
   * Fetch contravention code data
   */
  /* istanbul ignore next */
  const getContraventionCodeDatasource = useCallback(async () => {
    if (!effectiveContractId || !values?.locationId) {
      setContraventionCodeDatasource([])
      return
    }
    try {
      const response = await getCoreData(
        ManualReviewAPIEndpoints?.getContraventionCodesByLocation?.method,
        `${ManualReviewAPIEndpoints?.getContraventionCodesByLocation?.url
          .replace('__contractId__', effectiveContractId)
          .replace('__locationId__', values?.locationId)}`
      )
      setContraventionCodeDatasource(
        response?.status === utils?.apiResponseCode?.successCode
          ? response?.data || []
          : []
      )
    } catch {
      setContraventionCodeDatasource([])
      showToastMessage({
        message: 'Failed to load contravention codes.',
        status: 'error',
      })
    }
  }, [getCoreData, showToastMessage, effectiveContractId, values?.locationId])
  useEffect(() => {
    getContraventionCodeDatasource()
  }, [getContraventionCodeDatasource])

  useEffect(() => {
    getContraventionLocationData()
  }, [getContraventionLocationData])

  useEffect(() => {
    setDisplayContraventionCode(values?.contraventionCode || '')
  }, [values?.contraventionCode])

  // Clear madeChanges when edit mode is closed without saving
  useEffect(() => {
    if (!isEditingContravention) {
      setMadeChanges(false)
    }
  }, [isEditingContravention])

  return (
    <div className="offence-details-section">
      <h2 className="section-title">Offence Details</h2>

      <dl>
        {/* Reference ID */}
        <div className="NoticeDetailRow">
          <dt>{labels?.referenceId}</dt>
          <dd>
            <TruncatedText value={values?.referenceId} />
          </dd>
        </div>

        {/* Street ID */}
        <div className="NoticeDetailRow">
          <dt>{labels?.siteName}</dt>
          <dd>
            <TruncatedText value={values?.siteName} />
          </dd>
        </div>

        {/* Contravention Location */}
        <div className="NoticeDetailRow">
          <dt>{labels?.contraventionLocation}</dt>
          <dd>
            <TruncatedText value={displayContraventionLocation} />
          </dd>
        </div>

        {/* Contravention Code */}
        <div className="NoticeDetailRow contravention-code-row">
          <dt>{labels?.contraventionCode}</dt>
          {!isEditingContravention ? (
            <>
              <dd>
                <TruncatedText value={displayContraventionCode} />
              </dd>
              <div
                className="edit-icon-wrapper"
                onClick={handleEditClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleEditClick()
                  }
                }}
                aria-label="Edit Contravention Code"
              >
                {observationDetails?.taskStatus !== TASK_STATUS?.DISCARDED &&
                  observationDetails?.taskStatus !== TASK_STATUS?.ISSUED && (
                    <ReactSVG
                      src={EnableEditIcon}
                      alt=""
                      className="contravention-edit-icon"
                    />
                  )}
              </div>
            </>
          ) : (
            <div className="contravention-edit-container">
              <AutoComplete
                id={`id`}
                dataTestId={`${
                  contraventionCodeDatasource?.length > 0 ? `ac_id` : `id`
                }`}
                field={{
                  fieldLabel: '',
                  fieldValue: contraventionValue,
                  placeholder: 'Select Contravention Code',
                  dataSource: contraventionCodeDatasource || [],
                  valueField: 'id',
                  textField: 'codeWithDescription',
                  isSelect: false,
                  className: `colWiseControl`,
                  popperClassName: `decreasePopperHeight`,
                  disabled: false,
                  fieldAlert: '',
                }}
                value={contraventionValue?.id || ''}
                onChangeHandler={handleContraventionChange}
              />
              <div
                className={`save-icon-wrapper ${
                  isSaveDisabled ? 'save-icon-wrapper-disabled' : ''
                }`}
                onClick={!isSaveDisabled ? handleSaveClick : undefined}
                role="button"
                tabIndex={isSaveDisabled ? -1 : 0}
                onKeyDown={(e) => {
                  if (!isSaveDisabled && (e.key === 'Enter' || e.key === ' ')) {
                    handleSaveClick()
                  }
                }}
                aria-label="Save Contravention Code"
                aria-disabled={isSaveDisabled}
              >
                <SaveIcon
                  className={`${
                    isSaveDisabled
                      ? 'save-icon-disabled'
                      : 'contravention-save-icon'
                  } `}
                />
              </div>
            </div>
          )}
        </div>

        {/* Contravention Notes */}
        {values?.contraventionNote && (
          <div className="NoticeDetailRow contravention-notes-row">
            <dt>{labels?.contraventionNote}</dt>
            <dd className="contravention-notes-value">
              {values?.contraventionNote}
            </dd>
          </div>
        )}
      </dl>
    </div>
  )
}

export default OffenceDetailsSection
